import React, { useState, useEffect } from 'react';
import {
  Zap,
  Flame,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface TranslationDuelViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    answer: string;
    isCorrect: boolean;
    points: number;
  }) => void;
  isRoundResult: boolean;
}

export const TranslationDuelView: React.FC<TranslationDuelViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSelectedOption(null);
    setSubmitted(false);
  }, [round.id]);

  const handleSelect = (option: string) => {
    if (submitted || isRoundResult) return;
    setSelectedOption(option);
    setSubmitted(true);

    const isCorrect = option.toLowerCase() === round.correctAnswer.toLowerCase();
    const streak = (myPlayer.streakCount || 0) + (isCorrect ? 1 : 0);
    const earnedPoints = isCorrect ? 10 + Math.min(streak * 2, 10) : 0;

    if (isCorrect) {
      playCorrectSound();
    } else {
      playWrongSound();
    }

    onSubmit({
      answer: option,
      isCorrect,
      points: earnedPoints
    });
  };

  const options = round.options || [round.correctAnswer, 'opção alternativa', 'outra resposta', 'termo diferente'];

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* Target Word Card */}
      <div className="w-full bg-white rounded-3xl p-6 md:p-8 border border-amber-100 shadow-md text-center relative overflow-hidden">
        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">
          <Zap className="w-4 h-4 fill-current" />
          <span>Flash Translation Duel</span>
        </div>

        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight my-4">
          {round.targetText}
        </h2>

        {myPlayer.streakCount && myPlayer.streakCount > 1 ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-black shadow-xs">
            <Flame className="w-3.5 h-3.5 fill-current text-orange-500" />
            <span>{myPlayer.streakCount}x Streak Combo! (+bonus pts)</span>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Select the best Portuguese translation below</p>
        )}
      </div>

      {/* 4 Options Grid */}
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {options.map((option, idx) => {
          const isChosen = selectedOption === option;
          const isCorrect = option.toLowerCase() === round.correctAnswer.toLowerCase();

          let btnClass = 'bg-white hover:bg-amber-50 hover:border-amber-300 text-gray-800 border-gray-200';
          if (isRoundResult || submitted) {
            if (isCorrect) {
              btnClass = 'bg-emerald-500 text-white border-emerald-600 shadow-md scale-102';
            } else if (isChosen && !isCorrect) {
              btnClass = 'bg-red-500 text-white border-red-600 opacity-90';
            } else {
              btnClass = 'bg-gray-50 text-gray-400 border-gray-200 opacity-60';
            }
          }

          return (
            <button
              key={`${option}_${idx}`}
              onClick={() => handleSelect(option)}
              disabled={submitted || isRoundResult}
              className={`p-4 rounded-2xl border-2 font-bold text-base md:text-lg transition-all transform active:scale-95 flex items-center justify-between shadow-xs ${btnClass}`}
            >
              <span>{option}</span>
              {(isRoundResult || submitted) && isCorrect && <CheckCircle2 className="w-5 h-5 text-white" />}
              {(isRoundResult || submitted) && isChosen && !isCorrect && <XCircle className="w-5 h-5 text-white" />}
            </button>
          );
        })}
      </div>

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
          <span className={partnerPlayer.roundFinished ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
            {partnerPlayer.roundFinished ? '✓ Answered!' : 'Thinking...'}
          </span>
        </div>
      )}
    </div>
  );
};
